(function () {
  const SELECTOR = '[data-store-locator]';
  let i18nPackCache = null;

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      const existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        if (existing.dataset.loaded === 'true') return resolve();
        existing.addEventListener('load', resolve);
        existing.addEventListener('error', reject);
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', function () {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalizeMarkers(payload) {
    const list = Array.isArray(payload) ? payload : payload.markers || [];
    return list
      .filter(function (marker) {
        return marker.active !== false && marker.lat != null && marker.long != null;
      })
      .map(function (marker) {
        return {
          id: String(marker.id || marker.prestashopId),
          name: marker.name || '',
          address: marker.formattedAddress || '',
          notes: marker.notes || '',
          group: marker.group || 'all',
          lat: Number(marker.lat),
          lng: Number(marker.long != null ? marker.long : marker.lng),
        };
      });
  }

  function loadI18nPack(url) {
    if (i18nPackCache) return Promise.resolve(i18nPackCache);

    return fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to load store-locator-i18n.json');
        return response.json();
      })
      .then(function (data) {
        i18nPackCache = data;
        return data;
      });
  }

  function resolveLocaleStrings(pack, locale) {
    if (pack[locale]) return pack[locale];
    const short = locale.split('-')[0];
    if (pack[short]) return pack[short];
    return pack.en;
  }

  function StoreLocator(root) {
    this.root = root;
    this.mapEl = root.querySelector('[data-locator-map]');
    this.listEl = root.querySelector('[data-locator-list]');
    this.searchEl = root.querySelector('[data-locator-search]');
    this.countEl = root.querySelector('[data-locator-count]');
    this.loadingEl = root.querySelector('[data-locator-loading]');
    this.errorEl = root.querySelector('[data-locator-error]');
    this.filtersEl = root.querySelector('[data-locator-filters]');
    this.headingEl = root.querySelector('[data-i18n-heading]');

    this.locale = root.dataset.locale || 'en';
    this.i18nUrl = root.dataset.i18nUrl || '';
    this.customHeading = root.dataset.customHeading || '';
    this.apiKey = root.dataset.apiKey || '';
    this.markersUrl = root.dataset.markersUrl || '';
    this.defaultLat = parseFloat(root.dataset.defaultLat || '45.5');
    this.defaultLng = parseFloat(root.dataset.defaultLng || '10.8');
    this.defaultZoom = parseInt(root.dataset.defaultZoom || '5', 10);
    this.showGroups = root.dataset.showGroups === 'true';

    this.i18n = null;
    this.allMarkers = [];
    this.filteredMarkers = [];
    this.map = null;
    this.googleMarkers = [];
    this.clusterer = null;
    this.infoWindow = null;
    this.activeGroup = 'all';
    this.activeMarkerId = null;
  }

  StoreLocator.prototype.loadTranslations = function () {
    const self = this;

    return loadI18nPack(this.i18nUrl).then(function (pack) {
      self.i18n = resolveLocaleStrings(pack, self.locale);
      self.applyTranslations();
    });
  };

  StoreLocator.prototype.applyTranslations = function () {
    const strings = this.i18n;
    if (!strings) return;

    if (this.locale === 'ar') {
      this.root.setAttribute('dir', 'rtl');
    } else {
      this.root.removeAttribute('dir');
    }

    const heading = this.customHeading || strings.heading || '';
    if (this.headingEl) {
      if (heading) {
        this.headingEl.textContent = heading;
        this.headingEl.hidden = false;
      } else {
        this.headingEl.hidden = true;
      }
    }

    this.root.querySelectorAll('[data-i18n-filter]').forEach(function (button) {
      const key = button.getAttribute('data-i18n-filter');
      if (strings.filters && strings.filters[key]) {
        button.textContent = strings.filters[key];
      }
    });

    const searchLabel = this.root.querySelector('[data-i18n-search-label]');
    if (searchLabel && strings.search_label) {
      searchLabel.textContent = strings.search_label;
    }

    if (this.searchEl && strings.search_placeholder) {
      this.searchEl.placeholder = strings.search_placeholder;
    }

    if (this.loadingEl && strings.loading) {
      this.loadingEl.textContent = strings.loading;
    }

    if (this.mapEl && strings.map_aria_label) {
      this.mapEl.setAttribute('aria-label', strings.map_aria_label);
    }
  };

  StoreLocator.prototype.setError = function (message) {
    if (this.loadingEl) this.loadingEl.hidden = true;
    if (!this.errorEl) return;
    this.errorEl.hidden = false;
    this.errorEl.textContent = message;
  };

  StoreLocator.prototype.hideLoading = function () {
    if (this.loadingEl) this.loadingEl.hidden = true;
  };

  StoreLocator.prototype.loadGoogleMaps = function () {
    if (!this.apiKey) {
      throw new Error(this.i18n.errors.missing_api_key);
    }

    const mapsSrc =
      'https://maps.googleapis.com/maps/api/js?key=' +
      encodeURIComponent(this.apiKey);

    const clusterSrc =
      'https://unpkg.com/@googlemaps/markerclusterer@2.5.3/dist/index.min.js';

    return loadScript(mapsSrc).then(function () {
      return loadScript(clusterSrc);
    });
  };

  StoreLocator.prototype.fetchMarkers = function () {
    const self = this;

    return fetch(this.markersUrl)
      .then(function (response) {
        if (!response.ok) throw new Error(self.i18n.errors.load_markers);
        return response.json();
      })
      .then(normalizeMarkers.bind(null));
  };

  StoreLocator.prototype.formatStoresCount = function (count) {
    return (this.i18n.stores_count || '__COUNT__').replace('__COUNT__', String(count));
  };

  StoreLocator.prototype.formatGroupLabel = function (group) {
    if (this.i18n.filters && this.i18n.filters[group]) {
      return this.i18n.filters[group];
    }
    return group;
  };

  StoreLocator.prototype.initMap = function () {
    const self = this;

    this.map = new google.maps.Map(this.mapEl, {
      center: { lat: this.defaultLat, lng: this.defaultLng },
      zoom: this.defaultZoom,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });

    this.infoWindow = new google.maps.InfoWindow();

    google.maps.event.addListenerOnce(this.map, 'idle', function () {
      self.hideLoading();
    });
  };

  StoreLocator.prototype.renderList = function () {
    const self = this;
    this.listEl.innerHTML = '';

    this.filteredMarkers.forEach(function (marker) {
      const item = document.createElement('li');
      item.className = 'store-locator__item';
      item.dataset.markerId = marker.id;
      if (self.activeMarkerId === marker.id) item.classList.add('is-active');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'store-locator__item-button';
      button.innerHTML =
        '<span class="store-locator__item-name">' +
        escapeHtml(marker.name) +
        '</span>' +
        '<span class="store-locator__item-address">' +
        escapeHtml(marker.address) +
        '</span>' +
        (self.showGroups
          ? '<span class="store-locator__item-group">' +
            escapeHtml(self.formatGroupLabel(marker.group)) +
            '</span>'
          : '');

      button.addEventListener('click', function () {
        self.focusMarker(marker.id);
      });

      item.appendChild(button);
      self.listEl.appendChild(item);
    });

    if (this.countEl) {
      this.countEl.textContent = this.formatStoresCount(this.filteredMarkers.length);
    }
  };

  StoreLocator.prototype.renderMapMarkers = function () {
    const self = this;

    this.googleMarkers.forEach(function (marker) {
      marker.setMap(null);
    });
    this.googleMarkers = [];

    if (this.clusterer) {
      this.clusterer.clearMarkers();
      this.clusterer = null;
    }

    this.googleMarkers = this.filteredMarkers.map(function (marker) {
      const googleMarker = new google.maps.Marker({
        position: { lat: marker.lat, lng: marker.lng },
        title: marker.name,
      });

      googleMarker.__storeLocatorId = marker.id;

      googleMarker.addListener('click', function () {
        self.openInfoWindow(marker, googleMarker);
        self.setActiveItem(marker.id);
      });

      return googleMarker;
    });

    try {
      if (window.markerClusterer && this.googleMarkers.length) {
        this.clusterer = new markerClusterer.MarkerClusterer({
          map: this.map,
          markers: this.googleMarkers,
        });
      } else if (this.googleMarkers.length) {
        this.googleMarkers.forEach(function (googleMarker) {
          googleMarker.setMap(self.map);
        });
      }
    } catch (error) {
      this.googleMarkers.forEach(function (googleMarker) {
        googleMarker.setMap(self.map);
      });
    }
  };

  StoreLocator.prototype.openInfoWindow = function (marker, googleMarker) {
    const content =
      '<div class="store-locator__infowindow">' +
      '<strong>' +
      escapeHtml(marker.name) +
      '</strong>' +
      (marker.address ? '<p>' + escapeHtml(marker.address) + '</p>' : '') +
      (marker.notes ? '<div>' + marker.notes + '</div>' : '') +
      '</div>';

    this.infoWindow.setContent(content);
    this.infoWindow.open({ map: this.map, anchor: googleMarker });
  };

  StoreLocator.prototype.setActiveItem = function (markerId) {
    this.activeMarkerId = markerId;
    this.listEl.querySelectorAll('.store-locator__item').forEach(function (item) {
      item.classList.toggle('is-active', item.dataset.markerId === markerId);
    });

    const activeItem = this.listEl.querySelector('[data-marker-id="' + markerId + '"]');
    if (activeItem) activeItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  StoreLocator.prototype.focusMarker = function (markerId) {
    const marker = this.filteredMarkers.find(function (item) {
      return item.id === markerId;
    });
    const googleMarker = this.googleMarkers.find(function (item) {
      return item.__storeLocatorId === markerId;
    });

    if (!marker || !googleMarker) return;

    this.map.panTo({ lat: marker.lat, lng: marker.lng });
    this.map.setZoom(Math.max(this.map.getZoom(), 12));
    this.openInfoWindow(marker, googleMarker);
    this.setActiveItem(markerId);
  };

  StoreLocator.prototype.applyFilters = function () {
    const query = (this.searchEl && this.searchEl.value ? this.searchEl.value : '')
      .trim()
      .toLowerCase();

    this.filteredMarkers = this.allMarkers.filter(function (marker) {
      const matchesGroup = this.activeGroup === 'all' || marker.group === this.activeGroup;
      const haystack = (marker.name + ' ' + marker.address + ' ' + marker.group).toLowerCase();
      const matchesSearch = !query || haystack.indexOf(query) !== -1;
      return matchesGroup && matchesSearch;
    }, this);

    this.renderList();
    this.renderMapMarkers();
  };

  StoreLocator.prototype.bindEvents = function () {
    const self = this;

    if (this.searchEl) {
      this.searchEl.addEventListener('input', function () {
        self.applyFilters();
      });
    }

    if (this.filtersEl) {
      this.filtersEl.addEventListener('click', function (event) {
        const button = event.target.closest('[data-group]');
        if (!button) return;

        self.activeGroup = button.dataset.group;
        self.filtersEl.querySelectorAll('.store-locator__filter').forEach(function (el) {
          el.classList.toggle('is-active', el === button);
        });
        self.applyFilters();
      });
    }
  };

  StoreLocator.prototype.init = function () {
    const self = this;

    return this.loadTranslations()
      .then(function () {
        return self.fetchMarkers();
      })
      .then(function (markers) {
        self.allMarkers = markers;
        return self.loadGoogleMaps();
      })
      .then(function () {
        self.initMap();
        self.bindEvents();
        self.applyFilters();
        self.hideLoading();

        if (self.map) {
          google.maps.event.trigger(self.map, 'resize');
        }
      })
      .catch(function (error) {
        self.setError(error.message || (self.i18n && self.i18n.errors.generic) || 'Error');
      });
  };

  function boot() {
    document.querySelectorAll(SELECTOR).forEach(function (root) {
      new StoreLocator(root).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
